import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ModalForm } from "@/components/ModalForm";
import { Search, FileText, Sparkles, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { QueryDefinition } from "@/types/savedQuery";

interface CreateSavedQueryFormProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (name: string, description?: string, isPublic?: boolean) => Promise<void>;
	isLoading: boolean;
	currentFilters: QueryDefinition;
}

export const CreateSavedQueryForm = ({
	isOpen,
	onClose,
	onSubmit,
	isLoading,
	currentFilters
}: CreateSavedQueryFormProps) => {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [isPublic, setIsPublic] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		await onSubmit(name.trim(), description.trim() || undefined, isPublic);
		setName("");
		setDescription("");
		setIsPublic(false);
	};

	const handleClose = () => {
		setName("");
		setDescription("");
		setIsPublic(false);
		onClose();
	};

	// Count active filters
	const activeFilterCount = [
		currentFilters.searchTerm,
		currentFilters.skillsFilter?.length,
		currentFilters.statusFilter?.length,
		currentFilters.functionFilter,
		currentFilters.assignedToMe,
		currentFilters.showFavoritesOnly,
	].filter(Boolean).length;

	return (
		<ModalForm
			title={t("savedQueries.save", "Save Query")}
			isOpen={isOpen}
			onClose={handleClose}
			onSubmit={handleSubmit}
			submitText={isLoading ? t("savedQueries.form.creating", "Saving...") : t("savedQueries.form.create", "Save Query")}
			cancelText={t("common.cancel", "Cancel")}
			submitDisabled={!name.trim() || isLoading}
			className="max-w-2xl"
		>
			{/* Visual Header */}
			<div className="text-center mb-8">
				<div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-sm mb-4">
					<Search className="h-8 w-8 text-purple-600" />
				</div>
				<h3 className="text-lg font-semibold text-gray-900 mb-2">
					{t("savedQueries.save", "Save Query")}
				</h3>
				<p className="text-gray-600 text-sm">
					{t("savedQueries.form.subtitle", "Save your current filters to quickly apply them later")}
				</p>
			</div>

			{/* Form Fields */}
			<div className="space-y-6">
				{/* Name Field */}
				<div className="space-y-2">
					<Label htmlFor="name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
						<FileText className="h-4 w-4 text-gray-500" />
						{t("savedQueries.form.name", "Name")} *
					</Label>
					<Input
						id="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("savedQueries.form.namePlaceholder", "Enter query name")}
						className="h-11 text-base"
						required
					/>
					<p className="text-xs text-gray-500">
						{t("savedQueries.form.nameHelp", "Choose a descriptive name for your saved query")}
					</p>
				</div>

				{/* Description Field */}
				<div className="space-y-2">
					<Label htmlFor="description" className="text-sm font-medium text-gray-700 flex items-center gap-2">
						<Sparkles className="h-4 w-4 text-gray-500" />
						{t("savedQueries.form.description", "Description")}
					</Label>
					<Textarea
						id="description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder={t("savedQueries.form.descriptionPlaceholder", "Enter description (optional)")}
						className="min-h-[80px] text-base resize-none"
						rows={3}
					/>
					<p className="text-xs text-gray-500">
						{t("savedQueries.form.descriptionHelp", "Optional description to help you remember what this query is for")}
					</p>
				</div>

				{/* Public Toggle */}
				<div className="flex items-center justify-between p-4 bg-gray-50 rounded-sm">
					<div className="flex items-center gap-3">
						<Globe className="h-5 w-5 text-gray-500" />
						<div>
							<Label htmlFor="isPublic" className="text-sm font-medium text-gray-700">
								{t("savedQueries.form.isPublic", "Make public")}
							</Label>
							<p className="text-xs text-gray-500">
								{t("savedQueries.form.isPublicHelp", "Public queries can be seen by all organization members")}
							</p>
						</div>
					</div>
					<Switch
						id="isPublic"
						checked={isPublic}
						onCheckedChange={setIsPublic}
					/>
				</div>

				{/* Current Filters Preview */}
				<Card className="border-dashed border-2 border-gray-200 bg-gray-50">
					<CardContent className="p-4">
						<div className="text-sm font-medium text-gray-700 mb-3">
							{t("savedQueries.form.filtersPreview", "Filters to save")} ({activeFilterCount})
						</div>
						<div className="flex flex-wrap gap-2">
							{currentFilters.searchTerm && (
								<Badge variant="secondary">
									{t("common.search", "Search")}: {currentFilters.searchTerm}
								</Badge>
							)}
							{currentFilters.skillsFilter?.map((skill) => (
								<Badge key={skill} variant="secondary">
									{skill}
								</Badge>
							))}
							{currentFilters.statusFilter?.map((status) => (
								<Badge key={status} variant="secondary">
									{status}
								</Badge>
							))}
							{currentFilters.functionFilter && (
								<Badge variant="secondary">
									{t("candidates.function", "Function")}: {currentFilters.functionFilter}
								</Badge>
							)}
							{currentFilters.assignedToMe && (
								<Badge variant="secondary">
									{t("candidates.filters.assignedToMe", "Assigned to me")}
								</Badge>
							)}
							{currentFilters.showFavoritesOnly && (
								<Badge variant="secondary">
									{t("candidates.filters.favoritesOnly", "Favorites only")}
								</Badge>
							)}
							{activeFilterCount === 0 && (
								<span className="text-gray-500 text-sm">
									{t("savedQueries.form.noFilters", "No filters applied")}
								</span>
							)}
						</div>
					</CardContent>
				</Card>
			</div>
		</ModalForm>
	);
};
